import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { StatusBadge } from '@/components/hr/status-badge'
import { ModuleGuard } from '@/components/module-guard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

const expenseItemSchema = z.object({
  description: z.string().min(1, 'Description required'),
  amount: z.string().min(1, 'Amount required'),
  category: z.enum(['travel', 'meals', 'accommodation', 'supplies', 'other']).optional(),
  expenseDate: z.string().min(1, 'Date required'),
})

const createClaimSchema = z.object({
  employeeId: z.number().min(1, 'Employee required'),
  title: z.string().min(1, 'Title required'),
  description: z.string().optional(),
  currency: z.string().optional(),
  items: z.array(expenseItemSchema).min(1, 'At least one item required'),
})

type CreateClaimFormValues = z.infer<typeof createClaimSchema>

type ExpenseClaim = {
  id: number
  employeeId: number
  title: string
  status: string
  currency?: string | null
  totalAmount?: string | null
  submittedAt?: string | null
  createdAt?: string | null
  employee?: { firstName: string; lastName: string } | null
}

type Employee = {
  id: number
  firstName: string
  lastName: string
}

const EXPENSE_CATEGORIES = [
  'travel',
  'meals',
  'accommodation',
  'supplies',
  'other',
]

export const Route = createFileRoute('/hr/expense-claims/')({
  component: ExpenseClaimsPage,
})

function ExpenseClaimsPage() {
  return (
    <ModuleGuard moduleKey="hr">
      <ExpenseClaimsList />
    </ModuleGuard>
  )
}

function ExpenseClaimsList() {
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [employeeFilter, setEmployeeFilter] = useState<string>('all')
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [page] = useState(1)

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  type ClaimStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'paid'

  const { data: claimsData, isLoading } = useQuery(
    orpc.hr.listExpenseClaims.queryOptions({
      input: {
        status: statusFilter === 'all' ? undefined : (statusFilter as ClaimStatus),
        employeeId: employeeFilter !== 'all' ? Number(employeeFilter) : undefined,
        page,
        limit: 50,
      },
    }),
  )

  const { data: employeesData } = useQuery(
    orpc.hr.getEmployees.queryOptions({ input: { page: 1, limit: 500 } }),
  )

  const claims = (claimsData?.data ?? []) as ExpenseClaim[]
  const employees = ((employeesData as { data?: Employee[] } | null | undefined)?.data ?? []) as Employee[]

  const createMutation = useMutation(
    orpc.hr.createExpenseClaim.mutationOptions({
      onSuccess: () => {
        toast.success('Expense claim created')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listExpenseClaims']] })
        setIsFormOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const fmt = (val?: string | null) =>
    val ? Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'

  const getEmployeeName = (id: number, emp?: ExpenseClaim['employee']) => {
    if (emp) return `${emp.firstName} ${emp.lastName}`
    const found = employees.find((e) => e.id === id)
    return found ? `${found.firstName} ${found.lastName}` : `Employee #${id}`
  }

  const handleSubmit = async (data: CreateClaimFormValues) => {
    await createMutation.mutateAsync({
      ...data,
      items: data.items.map((item) => ({
        description: item.description,
        amount: Number(item.amount),
        category: item.category ?? 'other',
        expenseDate: item.expenseDate,
      })),
    })
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Expense Claims</h1>
          <p className="text-muted-foreground">Manage employee expense reimbursements</p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Claim
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>

            <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id.toString()}>
                    {emp.firstName} {emp.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : claims.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No expense claims found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {claims.map((claim) => (
                  <TableRow
                    key={claim.id}
                    className="cursor-pointer"
                    onClick={() => void navigate({ to: `/hr/expense-claims/${claim.id}` })}
                  >
                    <TableCell className="font-medium">{claim.title}</TableCell>
                    <TableCell>{getEmployeeName(claim.employeeId, claim.employee)}</TableCell>
                    <TableCell>{fmt(claim.totalAmount)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{claim.currency ?? 'USD'}</Badge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={claim.status} />
                    </TableCell>
                    <TableCell>
                      {claim.submittedAt
                        ? new Date(claim.submittedAt).toLocaleDateString()
                        : claim.createdAt
                          ? new Date(claim.createdAt).toLocaleDateString()
                          : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateClaimDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        employees={employees}
        onSubmit={handleSubmit}
      />
    </div>
  )
}

interface CreateClaimDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employees: Employee[]
  onSubmit: (data: CreateClaimFormValues) => Promise<void>
}

function CreateClaimDialog({ open, onOpenChange, employees, onSubmit }: CreateClaimDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<CreateClaimFormValues>({
    resolver: zodResolver(createClaimSchema),
    defaultValues: {
      title: '',
      description: '',
      currency: 'USD',
      items: [{ description: '', amount: '', category: 'other' as const, expenseDate: '' }],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'items',
  })

  const handleSubmit = async (data: CreateClaimFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Expense Claim</DialogTitle>
          <DialogDescription>Submit an expense reimbursement request</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="employeeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employee *</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(Number(v))}
                    value={field.value ? field.value.toString() : ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id.toString()}>
                          {emp.firstName} {emp.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input placeholder="Business travel expenses" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <FormControl>
                      <Input placeholder="USD" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Optional description..." rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <FormLabel>Expense Items *</FormLabel>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    append({ description: '', amount: '', category: 'other' as const, expenseDate: '' })
                  }
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Item
                </Button>
              </div>

              {fields.map((field, index) => (
                <div key={field.id} className="border rounded-md p-3 mb-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Item {index + 1}</span>
                    {fields.length > 1 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-red-600 h-6 w-6 p-0"
                        onClick={() => remove(index)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>

                  <FormField
                    control={form.control}
                    name={`items.${index}.description`}
                    render={({ field: f }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Description *</FormLabel>
                        <FormControl>
                          <Input placeholder="Flight ticket to NYC" {...f} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-3 gap-2">
                    <FormField
                      control={form.control}
                      name={`items.${index}.amount`}
                      render={({ field: f }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Amount *</FormLabel>
                          <FormControl>
                            <Input type="number" placeholder="0.00" {...f} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`items.${index}.category`}
                      render={({ field: f }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Category</FormLabel>
                          <Select onValueChange={f.onChange} value={f.value ?? 'other'}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {EXPENSE_CATEGORIES.map((cat) => (
                                <SelectItem key={cat} value={cat}>
                                  {cat.replace('_', ' ')}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`items.${index}.expenseDate`}
                      render={({ field: f }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Date *</FormLabel>
                          <FormControl>
                            <Input type="date" {...f} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Claim'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
